import type { LucideIcon } from 'lucide-react'
import { Tags } from 'lucide-react'

import {
  FilterMenuField,
  FilterOptionList,
  type FilterOption,
} from '@/components/filters/filter-menu'
import { MINIMAL_FILTER_FIELD_CLASS } from '@/components/filters/filter-styles'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { TalentFacet } from '@/lib/talent-browse-filters'
import { normalizeSkillKey, type SkillMatchMode } from '@/lib/talent-browse-filters'

const SKILL_MATCH_MODE_OPTIONS: FilterOption[] = [
  { value: 'and', label: 'Match all' },
  { value: 'or', label: 'Match any' },
]

export function facetsToSkillFilterOptions(facets: TalentFacet[]): FilterOption[] {
  return facets.map((facet) => ({
    value: facet.key,
    label: `${facet.label} (${facet.count})`,
  }))
}

export function selectedSkillKeysFromLabels(selectedSkills: string[]): string[] {
  return selectedSkills.map(normalizeSkillKey)
}

export function skillLabelsFromKeys(
  keys: string[],
  facets: TalentFacet[],
  currentLabels: string[],
): string[] {
  const facetByKey = new Map(facets.map((facet) => [facet.key, facet.label]))
  const labelByKey = new Map(currentLabels.map((label) => [normalizeSkillKey(label), label]))
  return keys.map((key) => facetByKey.get(key) ?? labelByKey.get(key) ?? key)
}

export type SkillMatchModeSelectProps = {
  value: SkillMatchMode
  onValueChange: (mode: SkillMatchMode) => void
}

export function SkillMatchModeSelect({ value, onValueChange }: SkillMatchModeSelectProps) {
  return (
    <Select value={value} onValueChange={(next) => onValueChange(next as SkillMatchMode)}>
      <SelectTrigger className={MINIMAL_FILTER_FIELD_CLASS}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SKILL_MATCH_MODE_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export type SkillFilterMenuContentProps = {
  facets: TalentFacet[]
  selectedSkills: string[]
  skillMatchMode: SkillMatchMode
  onSelectedSkillsChange: (skills: string[]) => void
  onSkillMatchModeChange: (mode: SkillMatchMode) => void
  showMatchMode?: boolean
}

export function SkillFilterMenuContent({
  facets,
  selectedSkills,
  skillMatchMode,
  onSelectedSkillsChange,
  onSkillMatchModeChange,
  showMatchMode = true,
}: SkillFilterMenuContentProps) {
  const options = facetsToSkillFilterOptions(facets)
  const selectedKeys = selectedSkillKeysFromLabels(selectedSkills)

  return (
    <div className="flex flex-col gap-2" onKeyDown={(event) => event.stopPropagation()}>
      {showMatchMode ? (
        <SkillMatchModeSelect value={skillMatchMode} onValueChange={onSkillMatchModeChange} />
      ) : null}
      <FilterOptionList
        type="multi"
        options={options}
        value={selectedKeys}
        onValueChange={(keys) =>
          onSelectedSkillsChange(skillLabelsFromKeys(keys, facets, selectedSkills))
        }
        searchPlaceholder="Search skills..."
        emptyLabel="No skills found"
      />
    </div>
  )
}

export type SkillFilterMenuFieldProps = SkillFilterMenuContentProps & {
  label?: string
  icon?: LucideIcon
  contentClassName?: string
}

export function SkillFilterMenuField({
  label = 'Skills',
  icon = Tags,
  contentClassName = 'w-64',
  ...contentProps
}: SkillFilterMenuFieldProps) {
  return (
    <FilterMenuField label={label} icon={icon} contentClassName={contentClassName}>
      <SkillFilterMenuContent {...contentProps} />
    </FilterMenuField>
  )
}
